import React, { createContext, useContext, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import it from "@/locales/it.json";
import en from "@/locales/en.json";

const dictionaries = { it, en };
export const SUPPORTED_LANGS = ["it", "en"];
export const DEFAULT_LANG = "it";

const I18nContext = createContext(null);

function resolveKey(dict, key) {
  const parts = key.split(".");
  let node = dict;
  for (const part of parts) {
    if (node == null) return undefined;
    node = node[part];
  }
  return node;
}

export function getLangFromPathname(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  if (SUPPORTED_LANGS.includes(first)) return first;
  return null;
}

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Thank-you page is Italian only; if no lang segment, fall back to default.
  const language = useMemo(() => {
    const detected = getLangFromPathname(location.pathname);
    return detected || DEFAULT_LANG;
  }, [location.pathname]);

  const t = useCallback(
    (key, fallback) => {
      const value = resolveKey(dictionaries[language], key);
      if (value === undefined || value === null) {
        const enValue = resolveKey(dictionaries.en, key);
        return enValue !== undefined ? enValue : fallback !== undefined ? fallback : key;
      }
      return value;
    },
    [language],
  );

  const switchLanguage = useCallback(
    (nextLang) => {
      if (!SUPPORTED_LANGS.includes(nextLang) || nextLang === language) return;
      const segments = location.pathname.split("/").filter(Boolean);
      const currentLang = getLangFromPathname(location.pathname);
      let nextSegments;
      if (currentLang) {
        nextSegments = [nextLang, ...segments.slice(1)];
      } else {
        // e.g. /thank_you or other non-lang page — keep as-is (thank-you is IT only)
        nextSegments = segments;
      }
      const nextPath = "/" + nextSegments.join("/");
      // Attach a marker so ScrollToTop can skip
      navigate(nextPath, { state: { languageSwitch: true }, replace: false });
    },
    [language, location.pathname, navigate],
  );

  const buildLocalizedPath = useCallback(
    (relativePath) => {
      const clean = relativePath.replace(/^\/+/, "");
      if (clean === "") return `/${language}`;
      return `/${language}/${clean}`;
    },
    [language],
  );

  const value = useMemo(
    () => ({ language, t, switchLanguage, buildLocalizedPath }),
    [language, t, switchLanguage, buildLocalizedPath],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
